import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6;
import { computed, flow, observable, runInAction } from "mobx";
import { formatNumber, realCommerceRequest, __ } from "../utils";
import { localizePaymentInterval } from "../others";
import { RealCommerceError } from "../exceptions";
import { License, ChangePlan } from ".";
import { routeLocationLicensePost, routeLocationSubscriptionChangePlanGet, routeLocationSubscriptionsDelete } from "@devowl-wp/api-real-commerce";
import { ESubscriptionStatus } from "@devowl-wp/api-real-commerce";
import { EProductPlanPaymentInterval } from "@devowl-wp/api-real-commerce";
let Subscription = (_class = class Subscription {
  /**
   * Save the last applied coupon of this transaction. This is necessary so we
   * can automatically safe the coupon when using for changing (upgrading) the plan.
   */

  get isOneTime() {
    return this.data.productPlan.paymentInterval === EProductPlanPaymentInterval.OneTime;
  }
  get humanReadablePaymentMethod() {
    return this.getHumanReadablePaymentMethod();
  }
  get canBeModified() {
    return !!this.data.ecommerceSystemSubscriptionId;
  }
  get isLifetime() {
    return !this.data.nextInvoicing && this.data.status === ESubscriptionStatus.Active;
  }
  get isInstallableProduct() {
    var _this$installations;
    return ((_this$installations = this.installations) === null || _this$installations === void 0 ? void 0 : _this$installations.length) > 0;
  }
  get installations() {
    return this.store.installations.get(this.data.id);
  }
  get productVariants() {
    return this.store.rpmProductVariantToSubscription.get(this.data.id);
  }
  get licenses() {
    return Array.from(this.store.licenses.values()).filter(l => l.data.subscription.id === this.data.id);
  }
  get labelPrice() {
    return formatNumber(+this.data.productPlan.price[0].recurringPrice);
  }
  get labelPaymentInterval() {
    return localizePaymentInterval(this.data.productPlan.paymentInterval);
  }

  // @TODO: dynamically get from REST API and move to product model
  get seller() {
    return {
      name: "Paddle.com",
      link: __("https://paddle.com"),
      terms: __("https://paddle.com/legal-buyers"),
      privacy: __("https://paddle.com/privacy-buyers")
    };
  }

  // @TODO: dynamically get from REST API and move to product model
  get manufacturer() {
    return {
      name: "devowl.io GmbH",
      link: __("https://devowl.io/"),
      terms: __("https://devowl.io/terms/"),
      cancellation: __("https://devowl.io/cancellation-policy/"),
      privacy: __("https://devowl.io/privacy-policy/"),
      support: __("https://devowl.io/support/"),
      supportDpa: __("https://devowl.io/dpa-support/")
    };
  }

  /**
   * Optional session as the subscription can also be modified without any login (e.g. `expired-payment-details` route).
   */

  constructor(data, store) {
    _initializerDefineProperty(this, "busy", _descriptor, this);
    _initializerDefineProperty(this, "sortIndex", _descriptor2, this);
    _initializerDefineProperty(this, "data", _descriptor3, this);
    _initializerDefineProperty(this, "lastFetchPlanError", _descriptor4, this);
    _initializerDefineProperty(this, "lastAppliedCoupon", _descriptor5, this);
    _initializerDefineProperty(this, "changePlan", _descriptor6, this);
    this.store = void 0;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.cancel = flow(function (_ref) {
      var _this = this;
      let {
        cancellationReason
      } = _ref;
      return function* () {
        _this.busy = true;
        yield _this.store.probablyRenewJwt();
        try {
          yield realCommerceRequest({
            location: routeLocationSubscriptionsDelete,
            request: {
              subscription: {
                id: _this.data.id,
                cancellationReason
              }
            },
            settings: {
              headers: _this.store.authorizationHeader
            }
          });

          // Reload all subscriptions
          _this.store.fetchSubscriptions();
        } catch (e) {
          console.log(e);
          throw new RealCommerceError(e);
        } finally {
          _this.busy = false;
        }
      }();
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.fetchChangePlans = flow(function* (coupon) {
      this.busy = true;
      yield this.store.probablyRenewJwt();
      try {
        const result = yield realCommerceRequest({
          location: routeLocationSubscriptionChangePlanGet,
          params: {
            subscriptionId: this.data.id,
            coupon
          },
          settings: {
            headers: this.store.authorizationHeader
          }
        });
        this.changePlan = [];
        for (const row of result.options) {
          this.changePlan.push(new ChangePlan(row, this));
        }
        this.lastAppliedCoupon = coupon;
      } catch (e) {
        console.log(e);
        this.lastFetchPlanError = new RealCommerceError(e);
        throw this.lastFetchPlanError;
      } finally {
        this.busy = false;
      }
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.addLicense = flow(function (_ref2) {
      var _this2 = this;
      let {
        rpmProductVariantId,
        ...rest
      } = _ref2;
      return function* () {
        yield _this2.store.probablyRenewJwt();
        try {
          const {
            license
          } = yield realCommerceRequest({
            location: routeLocationLicensePost,
            request: {
              subscription: {
                id: _this2.data.id
              },
              productPlanRpmProductVariant: {
                rpmProductVariantId
              },
              license: rest
            },
            settings: {
              headers: _this2.store.authorizationHeader
            }
          });
          _this2.store.licenses.set(license.id, new License(license, _this2.store));

          // Recalculate "Products" so "usage / maxUsage" count is corrected
          _this2.store.fetchSubscriptions();
        } catch (e) {
          console.log(e);
          throw new RealCommerceError(e);
        }
      }();
    });
    this.store = store;
    runInAction(() => {
      this.data = data;
    });
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "busy", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "sortIndex", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "data", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "lastFetchPlanError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "lastAppliedCoupon", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return "";
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "changePlan", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class.prototype, "isOneTime", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isOneTime"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "humanReadablePaymentMethod", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "humanReadablePaymentMethod"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canBeModified", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canBeModified"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isLifetime", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isLifetime"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isInstallableProduct", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isInstallableProduct"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "installations", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "installations"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "productVariants", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "productVariants"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "licenses", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "licenses"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "labelPrice", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "labelPrice"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "labelPaymentInterval", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "labelPaymentInterval"), _class.prototype)), _class);
export { Subscription };